import { Mui } from "@osu/react-ui";

/**
 * TextInput component using Material UI TextField from @osu/react-ui
 * @param {Object} props - Component props
 * @param {string} props.id - Input identifier
 * @param {string} props.label - Input label
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.helperText - Helper text to display below the input
 * @param {boolean} props.error - Whether to display the input in an error state
 * @param {Object} props.inputProps - Additional props to pass to the input element
 */

function TextInput(props = {}) {
  const {
    id,
    helperText,
    required = false,
    error = undefined,
    inputProps: _inputProps,
    modal,
    variant = "outlined",
    name,
    startAdornment,
    endAdornment,
    InputProps: _InputProps = {},
    label,
    description,
    compact,
    textFieldProps = {},
    value,
    setValue,
    defaultValue,
    onChange,
    area,
    multiline,
    rows,
    maxRows,
    minRows,
    slotProps: _slotProps = {},
    readOnly,
    round,
    truncateDecimals,
    transformOnBlur,
    ...rest
  } = props;
  const inputProps = {
    ..._inputProps || {}
  }

  const descriptionId = `${id}-description`;
  const inputLabelId = `${id}-label`;
  const slotProps = {
    ..._slotProps,
    input: {
      readOnly: readOnly,
      ..._slotProps?.input || {}
    },
  }

  let InputProps = {
    ..._InputProps,
  };
  if (startAdornment) {
    InputProps.startAdornment = (
      <Mui.InputAdornment position="start">{startAdornment}</Mui.InputAdornment>
    );
  }
  if (endAdornment) {
    InputProps.endAdornment = (
      <Mui.InputAdornment position="end">{endAdornment}</Mui.InputAdornment>
    );
  }

  const inputPropsForTextField = {
    multiline,
    rows,
    minRows,
    maxRows,

  };
  if (area) {
    inputPropsForTextField.multiline = true;
    const { rows, minRows, maxRows } = inputPropsForTextField;
    if ([rows, minRows, maxRows].every((field) => field === undefined)) {
      inputPropsForTextField.rows = 3;
    }
  }
  if (onChange || setValue) {
    inputPropsForTextField.onChange = (event) => {
      onChange && onChange(event.target.value);
      setValue && setValue(event.target.value, event.target.name || name);
    };
    inputPropsForTextField.value = value || "";
  } else if (defaultValue) {
    inputPropsForTextField.defaultValue = defaultValue;
  }

  if(round) {
    inputProps.onBlur = (event) => {
      const hasDecimal = event?.target?.value?.includes('.')
      const value = parseFloat(event?.target?.value)
      if (!isNaN(value)  && hasDecimal) {
        const rounded = Math.round(value)
        event.target.value = rounded
        onChange && onChange(rounded);
        setValue && setValue(rounded, event.target.name || name);
      }
    }
  } else if (truncateDecimals != null) {
    inputProps.onBlur = (event) => {
      const rawValue = event?.target?.value
      if (!rawValue?.includes('.')) return
      const value = parseFloat(rawValue)
      if (isNaN(value)) return
      const [whole, fraction] = rawValue.split('.')
      if (fraction?.length > truncateDecimals) {
        const truncated = `${whole}.${fraction.slice(0, truncateDecimals)}`
        event.target.value = truncated
        onChange && onChange(truncated);
        setValue && setValue(truncated, event.target.name || name);
      }
    }
  } else if (transformOnBlur) {
    inputProps.onBlur = (event) => {
      const val = event?.target?.value
      if (!val) return
      const transformed = transformOnBlur(val)
      if (transformed !== val) {
        event.target.value = transformed
        onChange && onChange(transformed);
        setValue && setValue(transformed, event.target.name || name);
      }
    }
  }

  if (compact) {
    let compactHelperText = "";
    if (description && helperText) {
      compactHelperText = [description, helperText].join(" - ");
    } else if (description) {
      compactHelperText = description;
    } else if (helperText) {
      compactHelperText = helperText;
    }

    return (
      <Mui.TextField
        id={id}
        required={required}
        name={name}
        error={error}
        label={label}
        helperText={compactHelperText}
        variant={variant}
        inputProps={{
          ...inputProps,
          "aria-required": required,
        }}
        InputProps={InputProps}
        {...rest}
        {...inputPropsForTextField}
        slotProps={slotProps}
      />
    );
  }

  return (
    <Mui.FormGroup>
      {label && (
        <Mui.FormLabel
          variant="body1"
          component="label"
          id={inputLabelId}
          required={required}
          error={error}
        >
          {label}
        </Mui.FormLabel>
      )}
      {description && (
        <Mui.Typography id={descriptionId} variant="body2" component="span">
          {description}
        </Mui.Typography>
      )}
      <Mui.TextField
         {...rest}
        {...(textFieldProps || {})}
        id={id}
        required={required}
        name={name}
        error={error}
        variant={variant}
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={label ? inputLabelId : undefined}
        inputProps={{
          ...inputProps,
          "aria-required": required,
        }}
        InputProps={InputProps}
        helperText={helperText}
        {...inputPropsForTextField}
        slotProps={slotProps}
      />
    </Mui.FormGroup>
  );
}

export default TextInput;
